import { ApiResponse } from '@shared/types';
import { PaginatedResponse } from '../types';
import {
  InventoryRecipeFilterParams,
  InventoryRecipeItemRecord,
  InventoryRecipeRecord,
  InventoryRecipeSummaryCounts,
  SaveRecipeRequest,
} from '../types/inventory';
import { supabase } from './supabase';

/** Returns a map of inventory_item_id → weighted-average cost across all stock locations */
export const fetchIngredientCosts = async (
  companyId: number,
  outletId?: string | null,
): Promise<Map<string, number>> => {
  let query = supabase
    .from('inventory_stock_balances')
    .select('inventory_item_id, average_cost, quantity_on_hand')
    .eq('company_id', companyId)
    .gt('quantity_on_hand', 0);

  if (outletId) query = query.eq('outlet_id', outletId);

  const { data } = await query;

  const costMap = new Map<string, number>();
  if (!data) return costMap;

  // Weighted average cost across all locations for each item
  const totals = new Map<string, { totalCost: number; totalQty: number }>();
  (data as Array<Record<string, unknown>>).forEach((row) => {
    const itemId = String(row.inventory_item_id || '');
    const qty = Number(row.quantity_on_hand || 0);
    const cost = Number(row.average_cost || 0);
    const cur = totals.get(itemId) ?? { totalCost: 0, totalQty: 0 };
    cur.totalCost += qty * cost;
    cur.totalQty += qty;
    totals.set(itemId, cur);
  });

  Array.from(totals).forEach(([itemId, { totalCost, totalQty }]) => {
    costMap.set(itemId, totalQty > 0 ? totalCost / totalQty : 0);
  });

  return costMap;
};

const DEFAULT_PAGE_SIZE = 20;

const toNumber = (value: unknown): number => {
  if (typeof value === 'number') return value;
  return Number(value || 0);
};

const mapRecipeItem = (
  row: Record<string, unknown>,
): InventoryRecipeItemRecord => ({
  id: String(row.id || ''),
  recipe_id: String(row.recipe_id || ''),
  inventory_item_id: String(row.inventory_item_id || ''),
  inventory_item_name:
    (row.inventory_item as { name?: string } | null)?.name ?? undefined,
  quantity: toNumber(row.quantity),
  unit_id: (row.unit_id as string | null) ?? null,
  notes: (row.notes as string | null) ?? null,
  created_at: String(row.created_at || ''),
  updated_at: String(row.updated_at || ''),
});

const mapRecipe = (row: Record<string, unknown>): InventoryRecipeRecord => ({
  id: String(row.id || ''),
  company_id: toNumber(row.company_id),
  outlet_id: (row.outlet_id as string | null) ?? null,
  menu_item_id: String(row.menu_item_id || ''),
  menu_item_name:
    (row.menu_item as { name?: string } | null)?.name ?? undefined,
  name: String(row.name || ''),
  description: (row.description as string | null) ?? null,
  yield_quantity: toNumber(row.yield_quantity),
  is_active: Boolean(row.is_active),
  created_by: (row.created_by as string | null) ?? null,
  created_at: String(row.created_at || ''),
  updated_at: String(row.updated_at || ''),
  items: Array.isArray(row.items)
    ? (row.items as Record<string, unknown>[]).map(mapRecipeItem)
    : undefined,
});

export const fetchRecipes = async ({
  companyId,
  outletId,
  status,
  page = 1,
  pageSize = DEFAULT_PAGE_SIZE,
  search,
}: InventoryRecipeFilterParams): Promise<
  ApiResponse<PaginatedResponse<InventoryRecipeRecord>>
> => {
  try {
    let query = supabase
      .from('inventory_recipes')
      .select(
        '*, menu_item:menu_items(name), items:inventory_recipe_items(*, inventory_item:inventory_items(name))',
        { count: 'exact' },
      )
      .eq('company_id', companyId)
      .order('name', { ascending: true });

    if (outletId && outletId !== 'all') {
      query = query.eq('outlet_id', outletId);
    }
    if (status === 'active') {
      query = query.eq('is_active', true);
    } else if (status === 'inactive') {
      query = query.eq('is_active', false);
    }
    if (search) {
      query = query.ilike('name', `%${search}%`);
    }

    const from = (page - 1) * pageSize;
    const to = from + pageSize - 1;
    query = query.range(from, to);

    const { data, error, count } = await query;
    if (error) {
      return { success: false, error: error.message, data: undefined };
    }

    const totalItems = count ?? 0;
    const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));

    return {
      success: true,
      data: {
        data: (data as Record<string, unknown>[]).map(mapRecipe),
        pagination: {
          page,
          pageSize,
          totalItems,
          totalPages,
          hasNextPage: page < totalPages,
          hasPreviousPage: page > 1,
        },
      },
    };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to fetch recipes',
      data: undefined,
    };
  }
};

export const fetchRecipeDetail = async (
  companyId: number,
  recipeId: string,
): Promise<ApiResponse<InventoryRecipeRecord>> => {
  try {
    const { data, error } = await supabase
      .from('inventory_recipes')
      .select(
        '*, menu_item:menu_items(name), items:inventory_recipe_items(*, inventory_item:inventory_items(name))',
      )
      .eq('company_id', companyId)
      .eq('id', recipeId)
      .single();

    if (error) {
      return { success: false, error: error.message, data: undefined };
    }
    return {
      success: true,
      data: mapRecipe(data as Record<string, unknown>),
    };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to fetch recipe',
      data: undefined,
    };
  }
};

export const fetchRecipeSummary = async (
  companyId: number,
  outletId: string,
  status: string,
): Promise<ApiResponse<InventoryRecipeSummaryCounts>> => {
  try {
    let query = supabase
      .from('inventory_recipes')
      .select('id, is_active, menu_item_id')
      .eq('company_id', companyId);

    if (outletId && outletId !== 'all') {
      query = query.eq('outlet_id', outletId);
    }
    if (status === 'active') {
      query = query.eq('is_active', true);
    } else if (status === 'inactive') {
      query = query.eq('is_active', false);
    }

    const { data, error } = await query;
    if (error) {
      return { success: false, error: error.message, data: undefined };
    }

    const rows = (data ?? []) as {
      id: string;
      is_active: boolean;
      menu_item_id: string;
    }[];
    const uniqueMenuItems = new Set(rows.map((r) => r.menu_item_id)).size;

    return {
      success: true,
      data: {
        totalRecipes: rows.length,
        activeRecipes: rows.filter((r) => r.is_active).length,
        inactiveRecipes: rows.filter((r) => !r.is_active).length,
        linkedMenuItems: uniqueMenuItems,
      },
    };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : 'Failed to fetch recipe summary',
      data: undefined,
    };
  }
};

export const saveRecipe = async (
  request: SaveRecipeRequest,
): Promise<ApiResponse<InventoryRecipeRecord>> => {
  try {
    const {
      id,
      companyId,
      outletId,
      menuItemId,
      name,
      description,
      yieldQuantity,
      isActive,
      items,
    } = request;

    const { data: recipe, error: recipeError } = await supabase
      .from('inventory_recipes')
      .upsert(
        {
          ...(id ? { id } : {}),
          company_id: companyId,
          outlet_id: outletId ?? null,
          menu_item_id: menuItemId,
          name,
          description: description ?? null,
          yield_quantity: yieldQuantity ?? 1,
          is_active: isActive ?? true,
        },
        { onConflict: 'id' },
      )
      .select()
      .single();

    if (recipeError) {
      return { success: false, error: recipeError.message, data: undefined };
    }

    const recipeId = (recipe as Record<string, unknown>).id as string;

    const { error: deleteError } = await supabase
      .from('inventory_recipe_items')
      .delete()
      .eq('recipe_id', recipeId);

    if (deleteError) {
      return { success: false, error: deleteError.message, data: undefined };
    }

    if (items.length > 0) {
      const { error: insertError } = await supabase
        .from('inventory_recipe_items')
        .insert(
          items.map((item) => ({
            recipe_id: recipeId,
            inventory_item_id: item.inventoryItemId,
            quantity: item.quantity,
            unit_id: item.unitId ?? null,
            notes: item.notes ?? null,
          })),
        );

      if (insertError) {
        return { success: false, error: insertError.message, data: undefined };
      }
    }

    return {
      success: true,
      data: mapRecipe(recipe as Record<string, unknown>),
    };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to save recipe',
      data: undefined,
    };
  }
};

export const deleteRecipe = async (
  companyId: number,
  recipeId: string,
): Promise<ApiResponse<void>> => {
  try {
    const { error } = await supabase
      .from('inventory_recipes')
      .delete()
      .eq('company_id', companyId)
      .eq('id', recipeId);

    if (error) {
      return { success: false, error: error.message, data: undefined };
    }
    return { success: true, data: undefined };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to delete recipe',
      data: undefined,
    };
  }
};
